import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import { getDynamicDepositAmount } from '@/app/components/search/components/DeferredPayments/BookNowPayLaterBanner/utils.ts';
import { type Feature, FeatureType } from '@/app/components/search/components/Card/utils/facilitiesTypes.ts';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import dynamic from 'next/dynamic';
import type { ModalProps } from '@/app/components/search/components/Card/Details/Features/getFeatures/types.ts';
import React from 'react';
import type { TransportData } from '@/domainLogic/index.ts';

export const GroupLuggageModal = dynamic(() => import('../../Modal/Modals/GroupLuggageModal'), {
  ssr: false,
});

export const getDepositAmountLabel = ({
  dealCard,
  localization,
  isDP,
  t,
}: {
  dealCard: DealCard;
  localization: Localization;
  isDP: boolean;
  searchParams: SearchParams;
  t: TranslationFunction;
}) => {
  const { total, pp } = getDynamicDepositAmount(dealCard, localization);
  if (isDP) return t('vacation.oss.deferred_payments.usp.amount_dp_pp', { amount: `<strong>${pp}</strong>` });
  return t('vacation.oss.cards.usp.book_now_pay_later_amount_ho', { amount: total });
};

const getSmallBagsCountText = ({ adultsChildrenCount, t }: { adultsChildrenCount: number; t: TranslationFunction }) =>
  adultsChildrenCount === 1
    ? t('vacation.select.cc.luggage.hand.single')
    : t('vacation.select.cc.luggage.hand.plural', { num: adultsChildrenCount.toString() });

const getCabinBagsCountText = ({
  transportData,
  adultsChildrenCount,
  t,
}: {
  transportData?: TransportData;
  adultsChildrenCount: number;
  t: TranslationFunction;
}) => {
  const hasHandBaggageIncluded = transportData?.inbound?.baggage?.hand;

  return hasHandBaggageIncluded
    ? adultsChildrenCount === 1
      ? t('vacation.select.cc.luggage.cabin.single')
      : t('vacation.select.cc.luggage.cabin.plural', { num: adultsChildrenCount.toString() })
    : null;
};

const getCheckedBagsCountText = ({
  hasGroupLuggageFeature,
  numberOfGroupLuggagePieces,
  transportData,
  adultsChildrenCount,
  t,
  localization,
  showFallback,
}: {
  hasGroupLuggageFeature: boolean;
  numberOfGroupLuggagePieces: number;
  transportData?: TransportData;
  adultsChildrenCount: number;
  t: TranslationFunction;
  localization: Localization;
  showFallback: boolean;
}) => {
  const hasCheckedBaggageIncluded = transportData?.inbound?.baggage?.hold;

  if (hasGroupLuggageFeature) {
    const isSinglePiece = numberOfGroupLuggagePieces === 1 || (hasCheckedBaggageIncluded && adultsChildrenCount === 1);
    const isMultiplePieces = numberOfGroupLuggagePieces > 1 || (hasCheckedBaggageIncluded && adultsChildrenCount > 1);

    if (isSinglePiece) {
      return t('vacation.select.cc.luggage.checked.total.single');
    }

    if (isMultiplePieces) {
      const pieces = numberOfGroupLuggagePieces > 1 ? numberOfGroupLuggagePieces : adultsChildrenCount;
      return t('vacation.select.cc.luggage.checked.total.plural', { num: pieces.toString() });
    }
  }

  if (hasCheckedBaggageIncluded) {
    if (adultsChildrenCount === 1) {
      return t('vacation.select.cc.luggage.checked.single');
    }

    if (adultsChildrenCount > 1) {
      return t('vacation.select.cc.luggage.checked.plural', { num: adultsChildrenCount.toString() });
    }
  }

  const priceFrom = transportData?.baggagePriceFrom;

  if (showFallback) {
    if (priceFrom) {
      const price = formatPrice({
        price: priceFrom,
        localization,
      });

      return t('vacation.oss.cards.flights.luggage_checked_from', { amount: `${price}` });
    }

    return t('vacation.oss.cards.flights.luggage_later');
  }

  return null;
};

const getHandUsp = ({ transportData, t }: { transportData?: TransportData; t: TranslationFunction }) => {
  const hasHandBaggageIncluded = transportData?.inbound?.baggage?.hand;

  if (hasHandBaggageIncluded) return t('vacation.oss.cards.flights.luggage_hand_included');
  return null;
};

const getHoldUsp = ({
  transportData,
  t,
  localization,
  showFallback,
}: {
  transportData?: TransportData;
  t: TranslationFunction;
  localization: Localization;
  showFallback: boolean;
}) => {
  const hasCheckedBaggageIncluded = transportData?.inbound?.baggage?.hold;
  if (hasCheckedBaggageIncluded) return t('vacation.oss.cards.flights.luggage_checked_included'); // 'Checked luggage included'

  const priceFrom = transportData?.baggagePriceFrom;

  if (showFallback) {
    if (priceFrom) {
      const price = formatPrice({
        price: priceFrom,
        localization,
      });

      return t('vacation.oss.cards.flights.luggage_checked_from', { amount: `${price}` });
    }

    return t('vacation.oss.cards.flights.luggage_later');
  }

  return null;
};

export const getBaggage = ({
  dealCard,
  transportData,
  t,
  localization,
  adultsChildrenCount,
  isMobile,
  showFallback = true,
  isOpx,
}: {
  dealCard: DealCard;
  transportData?: TransportData;
  t: TranslationFunction;
  localization: Localization;
  adultsChildrenCount: number;
  isMobile: boolean;
  showFallback: boolean;
  isOpx: boolean;
}): { small?: Feature; hand?: Feature; hold?: Feature } => {
  if (isOpx) return {};
  const numberOfGroupLuggagePieces = transportData?.includedTotalCheckedBags ?? 0;
  const hasGroupLuggageFeature = numberOfGroupLuggagePieces > 0;
  const showGroupLuggageTooltip = adultsChildrenCount > 1 && numberOfGroupLuggagePieces > 0 && !isMobile;
  const showGroupLuggageModal = adultsChildrenCount > 1 && numberOfGroupLuggagePieces > 0 && isMobile;

  const hasCheckedBaggageIncluded = transportData?.inbound?.baggage?.hold;

  const smallBagsCountText = getSmallBagsCountText({ adultsChildrenCount, t });
  const cabinBagsCountText = getCabinBagsCountText({
    transportData,
    adultsChildrenCount,
    t,
  });
  const checkedBagsCountText = getCheckedBagsCountText({
    hasGroupLuggageFeature,
    numberOfGroupLuggagePieces,
    transportData,
    adultsChildrenCount,
    t,
    localization,
    showFallback,
  });

  const handText = getHandUsp({ transportData, t });
  const holdText = getHoldUsp({ transportData, t, localization, showFallback });

  return hasGroupLuggageFeature
    ? {
        small: smallBagsCountText
          ? {
              text: smallBagsCountText,
              featureType: FeatureType.normal,
              tooltipSize: 213,
              iconRight:
                showGroupLuggageTooltip || showGroupLuggageModal
                  ? ({ category: 'Semantics', name: 'icon-circle-info', color: 'iconDisabled' } as const)
                  : undefined,
              className: 'group-luggage',
              ...(showGroupLuggageModal
                ? {
                    Modal: ({ isOpen, onClose }: ModalProps) =>
                      React.createElement(GroupLuggageModal, { isOpen, onClose, dealCard }),
                  }
                : {
                    tooltip: showGroupLuggageTooltip
                      ? t('vacation.oss.cards.flights.luggage.group.tooltip.hand')
                      : undefined,
                    tooltipSize: 213,
                  }),
            }
          : undefined,

        hand: cabinBagsCountText
          ? {
              text: cabinBagsCountText,
              featureType: FeatureType.normal,
              tooltipSize: 213,
              iconRight:
                showGroupLuggageTooltip || showGroupLuggageModal
                  ? ({ category: 'Semantics', name: 'icon-circle-info', color: 'iconDisabled' } as const)
                  : undefined,
              className: 'group-luggage',
              ...(showGroupLuggageModal
                ? {
                    Modal: ({ isOpen, onClose }: ModalProps) =>
                      React.createElement(GroupLuggageModal, { isOpen, onClose, dealCard }),
                  }
                : {
                    tooltip: showGroupLuggageTooltip
                      ? t('vacation.oss.cards.flights.luggage.group.tooltip.cabin')
                      : undefined,
                    tooltipSize: 213,
                  }),
            }
          : undefined,

        hold: checkedBagsCountText
          ? {
              icon: hasCheckedBaggageIncluded
                ? undefined
                : ({ category: 'UI Essentials', name: 'icon-bullet', color: 'iconPrimary' } as const),
              text: checkedBagsCountText,
              featureType: hasCheckedBaggageIncluded ? FeatureType.normal : FeatureType.gray,
              iconRight:
                showGroupLuggageTooltip || showGroupLuggageModal
                  ? ({ category: 'Semantics', name: 'icon-circle-info', color: 'iconDisabled' } as const)
                  : undefined,
              className:
                numberOfGroupLuggagePieces > 0 || hasCheckedBaggageIncluded
                  ? 'group-luggage group-luggage-bold'
                  : 'group-luggage',
              ...(showGroupLuggageModal
                ? {
                    Modal: ({ isOpen, onClose }: ModalProps) =>
                      React.createElement(GroupLuggageModal, { isOpen, onClose, dealCard }),
                  }
                : {
                    tooltip: showGroupLuggageTooltip
                      ? t('vacation.oss.cards.flights.luggage.group.tooltip.checked')
                      : undefined,
                    tooltipSize: 213,
                  }),
            }
          : undefined,
      }
    : {
        hand: handText
          ? {
              text: handText,
              featureType: FeatureType.normal,
            }
          : undefined,

        hold: holdText
          ? {
              icon: hasCheckedBaggageIncluded
                ? undefined
                : ({ category: 'UI Essentials', name: 'icon-bullet', color: 'iconPrimary' } as const),
              text: holdText,
              featureType: hasCheckedBaggageIncluded ? FeatureType.normal : FeatureType.gray,
            }
          : undefined,
      };
};
